import { useTranslation } from '../../../hooks';
import { StepComponentProps } from './props';

import styles from './styles.module.scss';
import { memo } from 'react';

/**
 * Step Component
 * A component that renders an individual step in a multi-step process, showing its state (active/completed)
 * and handling click interactions.
 *
 * @component
 * @template V - The type of the step value
 * @param {Object} props - Component props
 * @param {V} props.currentStep - The currently active step value
 * @param {string} props.i18nWorkspace - Translation namespace for i18n
 * @param {Object} props.option - Step option containing label and value
 * @param {boolean} props.blockFurtherSteps - Whether to prevent clicking on future steps
 * @param {number} props.index - Index of this step
 * @param {number} props.currentIndex - Current step index in the sequence
 * @param {boolean} props.isLoading - Whether the step is in loading state
 * @param {(value: V) => void} props.onStepClick - Callback when step is clicked
 * @returns {JSX.Element} The rendered step component
 *
 * @example
 * type StepValue = 'personal' | 'address' | 'payment';
 *
 * <Step
 *   currentStep="address"
 *   i18nWorkspace="checkout"
 *   option={{
 *     label: 'steps.address',
 *     value: 'address',
 *     isCompleted: true
 *   }}
 *   blockFurtherSteps={true}
 *   index={1}
 *   currentIndex={1}
 *   isLoading={false}
 *   onStepClick={(value) => function('Step clicked:', value)}
 * />
 */
function Step<V>({
  currentStep,
  i18nWorkspace,
  option,
  blockFurtherSteps,
  index,
  currentIndex,
  isLoading,
  onStepClick,
}: StepComponentProps<V>) {
  const { t } = useTranslation(i18nWorkspace);
  const isCurrent = currentIndex <= index;

  /**
   * Handles click events on the step
   * Calls onStepClick callback with the step value if defined
   */
  function handleClick() {
    if (typeof onStepClick != 'undefined') {
      onStepClick(option?.value);
    }
  }

  // Show loading skeleton if step is in loading state
  if (isLoading) {
    return (
      <div className={styles.step}>
        <div className={styles['step__indicator__skeleton']} />
        <div className={styles['step__label__skeleton']} />
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      className={`${styles.step} ${blockFurtherSteps ? (isCurrent ? styles['step--disabled'] : '') : ''}`}
      key={`step-${option.label}`}
    >
      <div
        className={`${styles['step__label']} ${option.value == currentStep ? styles['step__label--active'] : ''}`}
      >
        {t(option.label)}
      </div>
      <div
        className={`${styles.step__marker} ${option?.value == currentStep || option?.isCompleted ? styles['step__marker--completed'] : ''}`}
      />
    </div>
  );
}

export default memo(Step) as typeof Step;
